import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { backendUrl } from '../config';
import { useLanguage } from '../contexts/LanguageContext';

const AuthCallback = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const redirected = useRef(false);

  useEffect(() => {
    if (redirected.current) return;
    redirected.current = true;

    const handleCallback = async () => {
      // 1. Handle Google GIS redirect with ID token
      const credential = searchParams.get('credential');
      const code = searchParams.get('code');
      if (credential) {
        try {
          const { error } = await supabase.auth.signInWithIdToken({
            provider: 'google',
            token: credential
          });
          if (error) throw error;
          navigate('/');
          return;
        } catch (err) {
          navigate('/login');
          return;
        }
      }
      // 1b. Legacy code flow (if ever used)
      if (code) {
        try {
          const callbackUrl = `${backendUrl}/auth/google/callback?code=${encodeURIComponent(code)}`;
          window.location.href = callbackUrl;
          return;
        } catch (err) {
          navigate('/login');
          return;
        }
      }

      // 2. Handle magic link/session tokens (existing logic)
      const userId = searchParams.get('userId');
      const email = searchParams.get('email');
      const tempPassword = searchParams.get('tempPassword');
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const type = searchParams.get('type');
      const error = searchParams.get('error');

      if (error) {
        navigate('/login');
        return;
      }

      // Handle Google OAuth session tokens
      if (type === 'google_oauth' && accessToken && refreshToken) {
        try {
          // Set the session using the tokens
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });

          if (sessionError) {
            navigate('/login');
            return;
          }

          navigate('/');
          return;
        } catch (error) {
          navigate('/login');
          return;
        }
      }

      // Handle existing temp password flow
      if (!userId || !email || !tempPassword) {
        navigate('/login');
        return;
      }

      try {
        // Sign in with the temporary password
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password: tempPassword
        });

        if (signInError) {
          navigate('/login');
          return;
        }

        // Success! Redirect to dashboard
        navigate('/');
      } catch (error) {
        navigate('/login');
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div style={{ 
      maxWidth: '420px', 
      margin: '96px auto',
      background: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
      padding: '2.5rem 2rem',
      textAlign: 'center'
    }}>
      <h2 style={{ 
        marginBottom: '24px',
        color: '#2c6e49',
        fontWeight: '600'
      }}>{t('completingSignIn')}</h2>
      
      <div style={{ color: '#56ab2f', marginBottom: '16px' }}>
        {t('redirectingToCompleteAuth')}
      </div>
      
      <div style={{ 
        width: '40px', 
        height: '40px', 
        border: '4px solid #f3f3f3',
        borderTop: '4px solid #56ab2f',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
        margin: '0 auto'
      }}></div>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AuthCallback; 